import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, MapPin, Calendar, Star, ArrowRight, Clock, Plane, Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";

const BADGE_URLS = {
  gold: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/3qf4e4f0_Black%20And%20White%20Simple%20Icon%20Logo.png",
  silver: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/80ndwcjh_Black%20And%20White%20Simple%20Icon%20Logo%20%281%29.png",
  bronze: "https://customer-assets.emergentagent.com/job_travel-quality-label/artifacts/o5vrg8qt_Black%20And%20White%20Simple%20Icon%20Logo%20%282%29.png"
};

const HotelCard = ({ hotel, index }) => (
  <div 
    className="listing-card bg-white border border-gray-100 overflow-hidden group stagger-item"
    data-testid={`hotel-card-${index}`}
  >
    {/* Image */}
    <div className="relative h-56 image-zoom">
      <img 
        src={hotel.image_url} 
        alt={hotel.name}
        className="w-full h-full object-cover"
      />
      {/* Pending Review ribbon */}
      {hotel.review_status === "pending_review" && (
        <div className="absolute top-4 left-4">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-semibold shadow" data-testid="hotel-pending-review-badge">
            <Clock className="w-3 h-3" /> Pending Review
          </span>
        </div>
      )}
      {/* Rating Badge */}
      <div className="absolute top-4 right-4">
        <img 
          src={BADGE_URLS[hotel.rating]} 
          alt={`${hotel.rating} rating`}
          className="w-12 h-12 drop-shadow-lg"
        />
      </div>
    </div>

    {/* Content */}
    <div className="p-6">
      {/* Rating Tag */}
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border mb-3 ${
        hotel.rating === "gold" 
          ? "bg-[#FDF9EC] border-[#E8D38E] text-[#92710d]" 
          : hotel.rating === "silver" 
          ? "bg-[#F1F5F9] border-[#CBD5E1] text-[#64748b]" 
          : "bg-[#FAF2EA] border-[#E3BA94] text-[#a15f1c]"
      }`}>
        <Star className="w-3 h-3" />
        {hotel.rating.charAt(0).toUpperCase() + hotel.rating.slice(1)} Quality
      </div>

      <h3 className="font-heading text-xl font-bold text-[#0B132B] mb-2 group-hover:text-[#D4AF37] transition-colors">
        {hotel.name}
      </h3>
      
      <div className="flex items-center gap-1 text-[#718096] text-sm mb-3">
        <MapPin className="w-4 h-4" />
        <span>{hotel.location}, {hotel.country}</span>
      </div>

      <p className="text-[#4A5568] text-sm mb-4 line-clamp-2">
        {hotel.description}
      </p>

      {/* Amenities */}
      {hotel.amenities && hotel.amenities.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {hotel.amenities.slice(0, 3).map((amenity) => (
            <span 
              key={amenity}
              className="text-xs bg-[#F2F4F8] text-[#4A5568] px-2 py-1"
            >
              {amenity}
            </span>
          ))}
          {hotel.amenities.length > 3 && (
            <span className="text-xs text-[#718096]">+{hotel.amenities.length - 3} more</span>
          )}
        </div>
      )}

      {/* Certified Date & View Link */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-[#718096] text-xs">
          {hotel.review_status === "pending_review" ? (
            <span className="italic text-amber-700">Certification pending</span>
          ) : (
            <>
              <Calendar className="w-3 h-3" />
              <span>Certified: {hotel.certified_date}</span>
            </>
          )}
        </div>
        <Link 
          to={`/hotel/${hotel.id}`}
          className="text-[#D4AF37] hover:text-[#92710d] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all"
          data-testid={`hotel-link-${index}`}
        >
          View <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </div>
);

const DestinationCard = ({ destination, index }) => (
  <div 
    className="listing-card bg-white border border-gray-100 overflow-hidden group stagger-item"
    data-testid={`destination-card-${index}`}
  >
    {/* Image */}
    <div className="relative h-56 image-zoom">
      <img 
        src={destination.image_url} 
        alt={destination.name}
        className="w-full h-full object-cover"
      />
      {/* Pending Review ribbon */}
      {destination.review_status === "pending_review" && (
        <div className="absolute top-4 left-4">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-semibold shadow" data-testid="destination-pending-review-badge">
            <Clock className="w-3 h-3" /> Pending Review
          </span>
        </div>
      )}
      {/* Rating Badge */}
      <div className="absolute top-4 right-4">
        <img 
          src={BADGE_URLS[destination.rating]} 
          alt={`${destination.rating} rating`}
          className="w-12 h-12 drop-shadow-lg"
        />
      </div>
    </div>

    {/* Content */}
    <div className="p-6">
      {/* Rating Tag */}
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border mb-3 ${
        destination.rating === "gold" 
          ? "bg-[#FDF9EC] border-[#E8D38E] text-[#92710d]" 
          : destination.rating === "silver" 
          ? "bg-[#F1F5F9] border-[#CBD5E1] text-[#64748b]" 
          : "bg-[#FAF2EA] border-[#E3BA94] text-[#a15f1c]"
      }`}>
        <Star className="w-3 h-3" />
        {destination.rating.charAt(0).toUpperCase() + destination.rating.slice(1)} Destination
      </div>

      <h3 className="font-heading text-xl font-bold text-[#0B132B] mb-2 group-hover:text-[#D4AF37] transition-colors">
        {destination.name}
      </h3>
      
      <div className="flex items-center gap-1 text-[#718096] text-sm mb-3">
        <MapPin className="w-4 h-4" />
        <span>{destination.country}</span>
      </div>

      <p className="text-[#4A5568] text-sm mb-4 line-clamp-2">
        {destination.description}
      </p>

      {/* Highlights */}
      {destination.highlights && destination.highlights.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {destination.highlights.slice(0, 3).map((highlight) => (
            <span 
              key={highlight}
              className="text-xs bg-[#F2F4F8] text-[#4A5568] px-2 py-1"
            >
              {highlight}
            </span>
          ))}
          {destination.highlights.length > 3 && (
            <span className="text-xs text-[#718096]">+{destination.highlights.length - 3} more</span>
          )}
        </div>
      )}

      {/* View Link */}
      <div className="flex justify-end">
        <Link 
          to={`/destination/${destination.id}`}
          className="text-[#D4AF37] hover:text-[#92710d] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all"
          data-testid={`destination-link-${index}`}
        >
          Explore <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </div>
);

const AirlineCard = ({ airline, index }) => (
  <div 
    className="listing-card bg-white border border-gray-100 overflow-hidden group stagger-item"
    data-testid={`airline-card-${index}`}
  >
    <div className="relative h-56 image-zoom">
      <img 
        src={airline.image_url} 
        alt={airline.name}
        className="w-full h-full object-cover"
      />
      {airline.review_status === "pending_review" && (
        <div className="absolute top-4 left-4">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-semibold shadow" data-testid="airline-pending-review-badge">
            <Clock className="w-3 h-3" /> Pending Review
          </span>
        </div>
      )}
      <div className="absolute top-4 right-4">
        <img 
          src={BADGE_URLS[airline.rating]} 
          alt={`${airline.rating} rating`}
          className="w-12 h-12 drop-shadow-lg"
        />
      </div>
    </div>

    <div className="p-6">
      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold border mb-3 ${
        airline.rating === "gold" 
          ? "bg-[#FDF9EC] border-[#E8D38E] text-[#92710d]" 
          : airline.rating === "silver" 
          ? "bg-[#F1F5F9] border-[#CBD5E1] text-[#64748b]" 
          : "bg-[#FAF2EA] border-[#E3BA94] text-[#a15f1c]"
      }`}>
        <Plane className="w-3 h-3" />
        {airline.rating.charAt(0).toUpperCase() + airline.rating.slice(1)} Airline
      </div>

      <h3 className="font-heading text-xl font-bold text-[#0B132B] mb-2 group-hover:text-[#D4AF37] transition-colors">
        {airline.name}
      </h3>
      
      <div className="flex items-center gap-1 text-[#718096] text-sm mb-3">
        <MapPin className="w-4 h-4" />
        <span>{airline.country}</span>
      </div>

      <p className="text-[#4A5568] text-sm mb-4 line-clamp-2">
        {airline.description}
      </p>

      {airline.hubs && airline.hubs.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {airline.hubs.slice(0, 3).map((hub) => (
            <span key={hub} className="text-xs bg-[#F2F4F8] text-[#4A5568] px-2 py-1">
              {hub}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-[#718096] text-xs">
          {airline.review_status === "pending_review" ? (
            airline.fleet_size ? (
              <><Plane className="w-3 h-3" /><span>Fleet: {airline.fleet_size}</span></>
            ) : (
              <span className="italic text-amber-700">Certification pending</span>
            )
          ) : airline.fleet_size ? (
            <><Plane className="w-3 h-3" /><span>Fleet: {airline.fleet_size}</span></>
          ) : (
            <><Calendar className="w-3 h-3" /><span>Certified: {airline.certified_date}</span></>
          )}
        </div>
        <Link 
          to={`/airline/${airline.id}`}
          className="text-[#D4AF37] hover:text-[#92710d] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all"
          data-testid={`airline-link-${index}`}
        >
          Explore <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  </div>
);

const DirectorySection = ({ hotels, destinations, airlines, loading, onFilterHotels, onFilterDestinations, onFilterAirlines }) => {
  const { user, loading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState("hotels");
  const [hotelRating, setHotelRating] = useState("all");
  const [destinationRating, setDestinationRating] = useState("all");
  const [airlineRating, setAirlineRating] = useState("all");
  const [hotelSearch, setHotelSearch] = useState("");
  const [destinationSearch, setDestinationSearch] = useState("");
  const [airlineSearch, setAirlineSearch] = useState("");

  const handleHotelFilter = (rating) => {
    setHotelRating(rating);
    onFilterHotels(rating, hotelSearch);
  };

  const handleDestinationFilter = (rating) => {
    setDestinationRating(rating);
    onFilterDestinations(rating, destinationSearch);
  };

  const handleHotelSearch = (value) => {
    setHotelSearch(value);
    onFilterHotels(hotelRating, value);
  };

  const handleDestinationSearch = (value) => {
    setDestinationSearch(value);
    onFilterDestinations(destinationRating, value);
  };

  const handleAirlineFilter = (rating) => {
    setAirlineRating(rating);
    onFilterAirlines && onFilterAirlines(rating, airlineSearch);
  };

  const handleAirlineSearch = (value) => {
    setAirlineSearch(value);
    onFilterAirlines && onFilterAirlines(airlineRating, value);
  };

  return (
    <section 
      id="directory" 
      className="py-20 lg:py-32 bg-white"
      data-testid="directory-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-semibold mb-4">
            Certified Excellence
          </p>
          <h2 
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B132B] mb-6 tracking-tight"
            data-testid="directory-title"
          >
            Quality Directory
          </h2>
          <p className="text-[#4A5568] text-base sm:text-lg max-w-2xl mx-auto">
            Discover certified hotels, destinations and airlines that meet our rigorous quality standards
          </p>
        </div>

        {/* Auth gate */}
        {authLoading ? (
          <div className="text-center py-16" data-testid="directory-auth-loading">
            <div className="animate-spin w-10 h-10 border-4 border-[#D4AF37] border-t-transparent rounded-full mx-auto"></div>
          </div>
        ) : !user ? (
          <div 
            className="relative bg-gradient-to-br from-[#0B132B] to-[#1C2541] py-20 px-8 text-center"
            data-testid="directory-auth-wall"
          >
            <div className="absolute inset-0 opacity-10">
              <div className="w-full h-full" style={{ backgroundImage: "radial-gradient(circle at 30% 50%, #D4AF37 1px, transparent 1px)", backgroundSize: "24px 24px" }}></div>
            </div>
            <div className="relative max-w-xl mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
                <Lock className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="font-heading text-3xl font-bold text-white mb-4">
                Members-Only Directory
              </h3>
              <p className="text-white/70 text-base leading-relaxed mb-8 max-w-md mx-auto">
                The full WTTQ directory of certified hotels, destinations and airlines is reserved for approved partners, sponsors, investors and registered travellers.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/signin"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-[#D4AF37] text-[#0B132B] font-semibold text-sm hover:bg-[#E8C456] transition-colors"
                  data-testid="directory-signin-cta"
                >
                  Sign In
                </Link>
                <Link
                  to="/signin"
                  className="inline-flex items-center justify-center gap-2 px-7 py-3 bg-white/10 border border-white/30 text-white font-semibold text-sm hover:bg-white/20 transition-colors"
                  data-testid="directory-register-cta"
                >
                  Create Account
                </Link>
              </div>
              <p className="text-white/40 text-xs mt-6">
                New accounts require admin approval before accessing the directory.
              </p>
            </div>
          </div>
        ) : (
        <Tabs 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="w-full"
        >
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
            <TabsList className="bg-[#F2F4F8] p-1">
              <TabsTrigger 
                value="hotels"
                className="data-[state=active]:bg-white data-[state=active]:text-[#0B132B] data-[state=active]:shadow-sm px-6 py-2"
                data-testid="tab-hotels"
              >
                Quality Hotels
              </TabsTrigger>
              <TabsTrigger 
                value="destinations"
                className="data-[state=active]:bg-white data-[state=active]:text-[#0B132B] data-[state=active]:shadow-sm px-6 py-2"
                data-testid="tab-destinations"
              >
                Quality Destinations
              </TabsTrigger>
              <TabsTrigger 
                value="airlines"
                className="data-[state=active]:bg-white data-[state=active]:text-[#0B132B] data-[state=active]:shadow-sm px-6 py-2"
                data-testid="tab-airlines"
              >
                Quality Airlines
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Hotels Tab */}
          <TabsContent value="hotels">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#718096]" />
                <Input
                  placeholder="Search hotels by name, location, or country..."
                  value={hotelSearch}
                  onChange={(e) => handleHotelSearch(e.target.value)}
                  className="pl-10 h-12 border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                  data-testid="hotel-search-input"
                />
              </div>
              <Select value={hotelRating} onValueChange={handleHotelFilter}>
                <SelectTrigger 
                  className="w-full sm:w-48 h-12 border-gray-200"
                  data-testid="hotel-rating-filter"
                >
                  <SelectValue placeholder="Filter by rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="gold">Gold Quality</SelectItem>
                  <SelectItem value="silver">Silver Quality</SelectItem>
                  <SelectItem value="bronze">Bronze Quality</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Hotels Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="bg-gray-100 animate-pulse h-96 rounded"></div>
                ))}
              </div>
            ) : hotels.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="hotels-grid">
                {hotels.map((hotel, index) => (
                  <HotelCard key={hotel.id} hotel={hotel} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12" data-testid="hotels-empty">
                <p className="text-[#718096]">No hotels found matching your criteria.</p>
              </div>
            )}
          </TabsContent>

          {/* Destinations Tab */}
          <TabsContent value="destinations">
            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#718096]" />
                <Input
                  placeholder="Search destinations by name or country..."
                  value={destinationSearch}
                  onChange={(e) => handleDestinationSearch(e.target.value)}
                  className="pl-10 h-12 border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                  data-testid="destination-search-input"
                />
              </div>
              <Select value={destinationRating} onValueChange={handleDestinationFilter}>
                <SelectTrigger 
                  className="w-full sm:w-48 h-12 border-gray-200"
                  data-testid="destination-rating-filter"
                >
                  <SelectValue placeholder="Filter by rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="gold">Gold Destination</SelectItem>
                  <SelectItem value="silver">Silver Destination</SelectItem>
                  <SelectItem value="bronze">Bronze Destination</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Destinations Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="bg-gray-100 animate-pulse h-96 rounded"></div>
                ))}
              </div>
            ) : destinations.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="destinations-grid">
                {destinations.map((destination, index) => (
                  <DestinationCard key={destination.id} destination={destination} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12" data-testid="destinations-empty">
                <p className="text-[#718096]">No destinations found matching your criteria.</p>
              </div>
            )}
          </TabsContent>

          {/* Airlines Tab */}
          <TabsContent value="airlines">
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#718096]" />
                <Input
                  placeholder="Search airlines by name or country..."
                  value={airlineSearch}
                  onChange={(e) => handleAirlineSearch(e.target.value)}
                  className="pl-10 h-12 border-gray-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                  data-testid="airline-search-input"
                />
              </div>
              <Select value={airlineRating} onValueChange={handleAirlineFilter}>
                <SelectTrigger className="w-full sm:w-48 h-12 border-gray-200" data-testid="airline-rating-filter">
                  <SelectValue placeholder="Filter by rating" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="gold">Gold Airline</SelectItem>
                  <SelectItem value="silver">Silver Airline</SelectItem>
                  <SelectItem value="bronze">Bronze Airline</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="bg-gray-100 animate-pulse h-96 rounded"></div>
                ))}
              </div>
            ) : airlines && airlines.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="airlines-grid">
                {airlines.map((airline, index) => (
                  <AirlineCard key={airline.id} airline={airline} index={index} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12" data-testid="airlines-empty">
                <p className="text-[#718096]">No airlines found matching your criteria.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
        )}
      </div>
    </section>
  );
};

export default DirectorySection;
